import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import {
  tokens,
  fmtNumber,
  CodeChip,
  ClinicalEmptyState,
} from "../../shared/clinical-design";
import { SCORECARD_METRICS } from "../services/providerPerformanceService";

/** Barra horizontal sobria: provider (color) sobre peer (gris). */
const ComparativeBar = ({ metric, value, peer }) => {
  const pct = Math.min((value / metric.max) * 100, 100);
  const peerPct = Math.min((peer / metric.max) * 100, 100);
  const better = metric.higherIsBetter ? value >= peer : value <= peer;
  const delta = value - peer;
  const deltaLabel = `${delta >= 0 ? "▲" : "▼"} ${metric.fmt(Math.abs(delta))} vs peer`;

  return (
    <Box sx={{ mb: 1.4 }}>
      <Stack direction="row" alignItems="baseline" sx={{ mb: 0.4 }}>
        <Typography sx={{ fontSize: 12.5, color: tokens.ink2, flex: 1 }}>
          {metric.label}
        </Typography>
        <Typography
          sx={{
            fontFamily: tokens.fontMono,
            fontVariantNumeric: "tabular-nums",
            fontSize: 13.5,
            fontWeight: 600,
            color: tokens.ink,
            mr: 1,
          }}
        >
          {metric.fmt(value)}
        </Typography>
        <Typography
          sx={{
            fontFamily: tokens.fontMono,
            fontSize: 11,
            color: better ? tokens.ok : tokens.crit,
            whiteSpace: "nowrap",
          }}
        >
          {deltaLabel}
        </Typography>
      </Stack>
      <Box sx={{ height: 8, bgcolor: tokens.surface2, borderRadius: "2px", overflow: "hidden" }}>
        <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: better ? tokens.primary : tokens.warn }} />
      </Box>
      <Box sx={{ height: 4, bgcolor: tokens.surface2, borderRadius: "2px", overflow: "hidden", mt: "2px" }}>
        <Box sx={{ width: `${peerPct}%`, height: "100%", bgcolor: tokens.lineStrong }} />
      </Box>
    </Box>
  );
};

/**
 * Scorecard individual del provider vs promedio peer interno.
 * Provider strip denso + barras comparativas (§5.5 adaptado a provider).
 *
 * @param {Object} provider - scorecard seleccionado (o null)
 * @param {Object} peerAverages - { metricKey: avg }
 */
export default function ProviderScorecardPanel({ provider, peerAverages = {} }) {
  if (!provider) {
    return (
      <ClinicalEmptyState
        message="Select a provider from the list to review their scorecard against the internal peer average."
        sx={{ py: 6 }}
      />
    );
  }

  return (
    <Box>
      {/* Provider strip: una línea densa, sin avatar grande */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ pb: 1, mb: 1.5, borderBottom: `1px solid ${tokens.line}`, flexWrap: "wrap", rowGap: 0.5 }}
      >
        <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: tokens.ink }}>
          {provider.name}
        </Typography>
        <CodeChip code={`NPI ${provider.id}`} />
        <Typography sx={{ fontSize: 12.5, color: tokens.ink3 }}>
          {provider.center} · panel {fmtNumber(provider.panelSize)}
        </Typography>
      </Stack>

      {SCORECARD_METRICS.map((m) => (
        <ComparativeBar
          key={m.key}
          metric={m}
          value={provider[m.key]}
          peer={peerAverages[m.key] ?? 0}
        />
      ))}

      <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.6}>
          <Box sx={{ width: 14, height: 6, bgcolor: tokens.primary, borderRadius: "2px" }} />
          <Typography sx={{ fontSize: 11.5, color: tokens.ink3 }}>Provider</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.6}>
          <Box sx={{ width: 14, height: 6, bgcolor: tokens.lineStrong, borderRadius: "2px" }} />
          <Typography sx={{ fontSize: 11.5, color: tokens.ink3 }}>Peer average (12 PCPs)</Typography>
        </Stack>
      </Stack>
    </Box>
  );
}
